import React, { useState } from 'react';
import { Modal, Button, Typography, Card, Row, Col, Divider, Space, Tag, message, Form, Input, InputNumber, Select, DatePicker } from 'antd';
import { FileTextOutlined, DownloadOutlined, SendOutlined, CalculatorOutlined } from '@ant-design/icons';
import { useCart } from '../../contexts/CartContext';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

function QuoteGenerator({ visible, onCancel, onSuccess }) {
    const { 
        cartItems, 
        eventDetails, 
        serviceRequirements, 
        getCartTotal, 
        getCartItemsByVendor,
        clearCart 
    } = useCart();
    
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [quoteData, setQuoteData] = useState(null);

    const generateQuote = () => {
        const vendors = getCartItemsByVendor();
        const totalAmount = getCartTotal();
        
        // Calculate service costs
        const serviceCosts = {
            setup: serviceRequirements.setup ? 5000 : 0,
            serving: serviceRequirements.serving ? 3000 : 0,
            cleanup: serviceRequirements.cleanup ? 2000 : 0,
            staff: serviceRequirements.staffCount * 1000,
            equipment: serviceRequirements.equipment.length * 500
        };
        
        const totalServiceCost = Object.values(serviceCosts).reduce((sum, cost) => sum + cost, 0);
        const finalTotal = totalAmount + totalServiceCost;

        const quote = {
            id: `QUOTE-${Date.now()}`,
            date: dayjs().format('YYYY-MM-DD'),
            validUntil: dayjs().add(7, 'days').format('YYYY-MM-DD'),
            event: {
                date: eventDetails.date,
                time: eventDetails.time,
                venue: eventDetails.venue,
                occasion: eventDetails.occasion,
                guestCount: eventDetails.guestCount,
                specialInstructions: eventDetails.specialInstructions
            },
            vendors: vendors.map(vendor => ({
                ...vendor,
                subtotal: vendor.items.reduce((sum, item) => sum + (item.price * item.quantity), 0)
            })),
            services: {
                ...serviceRequirements,
                costs: serviceCosts
            },
            pricing: {
                subtotal: totalAmount,
                serviceCost: totalServiceCost,
                tax: finalTotal * 0.18, // 18% GST
                total: finalTotal + (finalTotal * 0.18)
            },
            terms: [
                'Quote valid for 7 days from issue date',
                '50% advance payment required to confirm booking',
                'Final payment due 48 hours before event',
                'Cancellation policy: 50% refund if cancelled 7+ days before event',
                'Prices subject to change based on final guest count'
            ]
        };

        setQuoteData(quote);
        return quote;
    };

    const handleSubmitQuote = async (values) => {
        setLoading(true);
        try {
            const quote = generateQuote();
            
            // Prepare quote data for API
            const packages = (quote.vendors[0]?.items || []).map(item => ({
                id: item.id,
                name: item.name,
                description: item.description || '',
                price: parseFloat(item.price) || 0,
                quantity: parseInt(item.quantity) || 1 // Ensure quantity is always present and valid
            }));

            const quoteData = {
                vendor_id: quote.vendors[0]?.vendor_id, // Assuming single vendor for now
                event_details: quote.event,
                service_requirements: quote.services,
                packages: packages,
                special_instructions: quote.event.specialInstructions,
                customer_notes: values.notes
            };

            const response = await api.post('/quotes', quoteData);
            
            message.success('Quote request submitted successfully!');
            onSuccess?.(response.data.data);
            
            // Clear cart after successful submission
            clearCart();
            
        } catch (error) {
            message.error('Failed to submit quote request');
            console.error('Quote submission error:', error);
        } finally {
            setLoading(false);
        }
    };

    const downloadQuote = () => {
        if (!quoteData) return;
        
        // Generate PDF content (simplified)
        const content = `
QUOTE #${quoteData.id}
Date: ${quoteData.date}
Valid Until: ${quoteData.validUntil}

EVENT DETAILS:
Date: ${quoteData.event.date}
Time: ${quoteData.event.time}
Venue: ${quoteData.event.venue}
Occasion: ${quoteData.event.occasion}
Guests: ${quoteData.event.guestCount}

VENDORS & ITEMS:
${quoteData.vendors.map(vendor => `
${vendor.vendor_name}:
${vendor.items.map(item => `- ${item.name} x${item.quantity} = ₹${item.price * item.quantity}`).join('\n')}
Subtotal: ₹${vendor.subtotal}
`).join('\n')}

SERVICES:
${Object.entries(quoteData.services.costs).map(([service, cost]) => 
    cost > 0 ? `${service}: ₹${cost}` : ''
).filter(Boolean).join('\n')}

PRICING:
Subtotal: ₹${quoteData.pricing.subtotal}
Service Cost: ₹${quoteData.pricing.serviceCost}
Tax (18%): ₹${quoteData.pricing.tax}
TOTAL: ₹${quoteData.pricing.total}

TERMS & CONDITIONS:
${quoteData.terms.map(term => `• ${term}`).join('\n')}
        `;
        
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `quote-${quoteData.id}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const renderQuotePreview = () => {
        if (!quoteData) return null;

        return (
            <div className="space-y-6">
                <Card>
                    <div className="text-center mb-4">
                        <Title level={3}>Quote #{quoteData.id}</Title>
                        <Text type="secondary">
                            Valid until {dayjs(quoteData.validUntil).format('MMM DD, YYYY')}
                        </Text>
                    </div>

                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            <Text strong>Event Date:</Text>
                            <br />
                            <Text>{dayjs(quoteData.event.date).format('MMM DD, YYYY')} at {quoteData.event.time}</Text>
                        </Col>
                        <Col span={12}>
                            <Text strong>Venue:</Text>
                            <br />
                            <Text>{quoteData.event.venue}</Text>
                        </Col>
                        <Col span={12}>
                            <Text strong>Occasion:</Text>
                            <br />
                            <Text>{quoteData.event.occasion}</Text>
                        </Col>
                        <Col span={12}>
                            <Text strong>Guest Count:</Text>
                            <br />
                            <Text>{quoteData.event.guestCount} guests</Text>
                        </Col>
                    </Row>
                </Card>

                <Card title="Vendors & Items">
                    {quoteData.vendors.map((vendor, index) => (
                        <div key={index} className="mb-4">
                            <Text strong className="text-lg">{vendor.vendor_name}</Text>
                            <div className="mt-2 space-y-1">
                                {vendor.items.map((item, itemIndex) => (
                                    <div key={itemIndex} className="flex justify-between">
                                        <Text>{item.name} x {item.quantity}</Text>
                                        <Text>₹{(item.price * item.quantity).toLocaleString()}</Text>
                                    </div>
                                ))}
                            </div>
                            <Divider />
                            <div className="flex justify-between font-semibold">
                                <Text>Subtotal:</Text>
                                <Text>₹{vendor.subtotal.toLocaleString()}</Text>
                            </div>
                        </div>
                    ))}
                </Card>

                <Card title="Services">
                    <div className="space-y-2">
                        {Object.entries(quoteData.services.costs).map(([service, cost]) => (
                            cost > 0 && (
                                <div key={service} className="flex justify-between">
                                    <Text className="capitalize">{service.replace(/([A-Z])/g, ' $1')}</Text>
                                    <Text>₹{cost.toLocaleString()}</Text>
                                </div>
                            )
                        ))}
                    </div>
                </Card>

                <Card title="Pricing Summary">
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <Text>Subtotal:</Text>
                            <Text>₹{quoteData.pricing.subtotal.toLocaleString()}</Text>
                        </div>
                        <div className="flex justify-between">
                            <Text>Service Cost:</Text>
                            <Text>₹{quoteData.pricing.serviceCost.toLocaleString()}</Text>
                        </div>
                        <div className="flex justify-between">
                            <Text>Tax (18%):</Text>
                            <Text>₹{quoteData.pricing.tax.toLocaleString()}</Text>
                        </div>
                        <Divider />
                        <div className="flex justify-between text-lg font-bold">
                            <Text>Total:</Text>
                            <Text className="text-orange-600">₹{quoteData.pricing.total.toLocaleString()}</Text>
                        </div>
                    </div>
                </Card>
            </div>
        );
    };

    return (
        <Modal
            title={
                <div className="flex items-center gap-2">
                    <FileTextOutlined />
                    <span>Generate Quote</span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            width={800}
            footer={null}
        >
            <div className="space-y-6">
                {!quoteData ? (
                    <div className="text-center py-8">
                        <CalculatorOutlined className="text-4xl text-gray-300 mb-4" />
                        <Title level={4}>Ready to Generate Quote</Title>
                        <Paragraph type="secondary">
                            Your cart contains {cartItems.length} items for {eventDetails.guestCount || 0} guests
                        </Paragraph>
                        <Button 
                            type="primary" 
                            size="large"
                            onClick={generateQuote}
                            className="mt-4"
                        >
                            Generate Quote
                        </Button>
                    </div>
                ) : (
                    <>
                        {renderQuotePreview()}
                        
                        <Card title="Customer Information">
                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={handleSubmitQuote}
                            >
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Full Name"
                                            name="name"
                                            rules={[{ required: true, message: 'Please enter your name' }]}
                                        >
                                            <Input placeholder="Enter your full name" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Phone Number"
                                            name="phone"
                                            rules={[{ required: true, message: 'Please enter your phone number' }]}
                                        >
                                            <Input placeholder="Enter your phone number" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                
                                <Form.Item
                                    label="Email Address"
                                    name="email"
                                    rules={[
                                        { required: true, message: 'Please enter your email' },
                                        { type: 'email', message: 'Please enter a valid email' }
                                    ]}
                                >
                                    <Input placeholder="Enter your email address" />
                                </Form.Item>

                                <Form.Item
                                    label="Company/Organization (Optional)"
                                    name="company"
                                >
                                    <Input placeholder="Enter company name" />
                                </Form.Item>

                                <Form.Item
                                    label="Additional Notes"
                                    name="notes"
                                >
                                    <TextArea 
                                        rows={3} 
                                        placeholder="Any additional requirements or questions..."
                                    />
                                </Form.Item>

                                <div className="flex gap-3 justify-end">
                                    <Button onClick={onCancel}>
                                        Cancel
                                    </Button>
                                    <Button 
                                        icon={<DownloadOutlined />}
                                        onClick={downloadQuote}
                                    >
                                        Download Quote
                                    </Button>
                                    <Button 
                                        type="primary" 
                                        icon={<SendOutlined />}
                                        htmlType="submit"
                                        loading={loading}
                                    >
                                        Submit Quote Request
                                    </Button>
                                </div>
                            </Form>
                        </Card>
                    </>
                )}
            </div>
        </Modal>
    );
}

export default QuoteGenerator;
